import { Injectable, inject } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ErrorModalComponent } from "../modules/modals/error-modal/error-modal.component";
import { InfoModalComponent } from "../modules/modals/info-modal/info-modal.component";
import { LoadingModalComponent } from "../modules/modals/loading-modal/loading-modal.component";
import { ModalContent } from "../interfaces/modal-content";
import { DeleteModalComponent } from "../modules/modals/delete-modal/delete-modal.component";
import { User } from "../interfaces/users/user";
import { IUserConfig } from "../interfaces/iuser-config";
import { Theme } from "../enums/Theme";
import { ERoles } from "../enums/eroles";
import { InfoModalConfig } from "../entities/core/info-modal-config";

@Injectable({
	providedIn: "root",
})
export class MitpManagerService {
	private dialog = inject(MatDialog);

	private getStoredRoles(user?: User): string[] {
		if (user && Array.isArray(user.roles)) {
			return user.roles;
		}

		const storedRoles = sessionStorage.getItem("roles");
		if (!storedRoles) {
			return [];
		}

		try {
			const parsedRoles = JSON.parse(storedRoles);
			return Array.isArray(parsedRoles) ? parsedRoles : [];
		} catch (error) {
			console.error("Error parsing roles from sessionStorage:", error);
			return [];
		}
	}

	static isEmailAddress(email: string): boolean {
		const re =
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		return re.test(email);
	}

	isGranted(role: string): boolean {
		const roles = this.getStoredRoles();
		switch (role) {
			case ERoles.ROLE_ADMIN_DIRECTORY:
				return roles.includes(ERoles.ROLE_ADMIN_DIRECTORY);
			case ERoles.ROLE_ADMIN_DIRECTORY_FULL:
				return roles.includes(ERoles.ROLE_ADMIN_DIRECTORY_FULL) || roles.includes(ERoles.ROLE_SUPREME_ADMIN);
			case ERoles.ROLE_SUPREME_ADMIN:
				return roles.includes(ERoles.ROLE_SUPREME_ADMIN);
		}
		return false;
	}

	isAdmin(): boolean {
		return this.isGranted(ERoles.ROLE_SUPREME_ADMIN);
	}

	isNotificationsGranted(): boolean {
		//return this.isAdmin() || this.isAdminUsers() || this.isManager();
		return this.isAdmin();
	}

	isManager(user?: User): boolean {
		const roles = this.getStoredRoles(user);
		if (roles.length === 0) {
			return false;
		}
		return roles.includes(ERoles.ROLE_MANAGER);
	}

	isPathwaysManager(user?: User): boolean {
		const roles = this.getStoredRoles(user);
		if (roles.length === 0) {
			return false;
		}
		return roles.includes(ERoles.ROLE_PATHWAYS_MANAGER);
	}

	isPharmacist(user?: User): boolean {
		const roles = this.getStoredRoles(user);
		if (roles.length === 0) {
			return false;
		}
		return roles.includes(ERoles.ROLE_PHARMACY);
	}

	isEducationManager(user?: User): boolean {
		const roles = this.getStoredRoles(user);
		if (roles.length === 0) {
			return false;
		}
		return roles.includes(ERoles.ROLE_EDUCATION_MANAGER);
	}

	isEducationStudent(user?: User): boolean {
		const roles = this.getStoredRoles(user);
		if (roles.length === 0) {
			return false;
		}
		return roles.includes(ERoles.ROLE_EDUCATION_STUDENT);
	}

	isCoder(user?: User): boolean {
		const roles = this.getStoredRoles(user);
		if (roles.length === 0) {
			return false;
		}
		return roles.includes(ERoles.ROLE_CODER);
	}

	isQuality(user?: User): boolean {
		const roles = this.getStoredRoles(user);
		if (roles.length === 0) {
			return false;
		}
		return roles.includes(ERoles.ROLE_QUALITY);
	}

	isAdminDirectory(user?: User): boolean {
		const roles = this.getStoredRoles(user);
		if (roles.length === 0) {
			return false;
		}
		return (
			roles.includes(ERoles.ROLE_ADMIN_DIRECTORY) ||
			roles.includes(ERoles.ROLE_ADMIN_DIRECTORY_FULL) ||
			roles.includes(ERoles.ROLE_SUPREME_ADMIN)
		);
	}

	/** Grants and revokes Catalog bundles without payment; the supreme admin inherits it. */
	isCatalogAdmin(user?: User): boolean {
		const roles = this.getStoredRoles(user);
		return roles.includes(ERoles.ROLE_ADMIN_CATALOG) || roles.includes(ERoles.ROLE_SUPREME_ADMIN);
	}

	/** Creates and publishes Catalog bundles; the supreme admin inherits ROLE_MANAGER. */
	isCatalogManager(user?: User): boolean {
		const roles = this.getStoredRoles(user);
		return roles.includes(ERoles.ROLE_MANAGER) || roles.includes(ERoles.ROLE_SUPREME_ADMIN);
	}

	isQualityRole(): boolean {
		const roles = this.getStoredRoles();
		if (roles.length === 0) {
			return false;
		}
		return roles.includes(ERoles.ROLE_QUALITY);
	}

	isAdminUsers(user?: User): boolean {
		const roles = this.getStoredRoles(user);
		if (roles.length === 0) {
			return false;
		}
		const validation = (role) =>
			role === ERoles.ROLE_SUPREME_ADMIN ||
			role === ERoles.ROLE_ADMIN_USERS_FULL ||
			role.startsWith("ROLE_ADMIN_USERS_DEPARTMENT") ||
			role.startsWith("ROLE_ADMIN_USERS_INSTITUTE");
		return roles.some(validation);
	}

	showLoading(value: boolean) {
		if (value) {
			this.dialog.open(LoadingModalComponent, { id: "loadingModal" });
		} else {
			this.dialog.getDialogById("loadingModal")?.close();
		}
	}

	openErrorDialog(message: string): void {
		this.dialog.open(ErrorModalComponent, {
			data: message,
			disableClose: true,
			width: "500px",
		});
	}

	openInfoDialog(message: string, config?: InfoModalConfig): MatDialogRef<InfoModalComponent> {
		return this.dialog.open(InfoModalComponent, {
			data: { message, config },
			disableClose: true,
		});
	}

	showConfirmDialog(message: string, positiveButton?: string, title?: string) {
		const dialogContent: ModalContent = {
			message: message,
			positiveButton: positiveButton ? positiveButton : "Supprimer",
			title: title ? title : "MedInThePocket",
		};
		return this.dialog.open(DeleteModalComponent, { data: dialogContent });
	}

	saveToTemp(key: string, value: string): void {
		sessionStorage.setItem(key, value);
	}

	readFromTemp(key: string): string | null {
		return sessionStorage.getItem(key);
	}

	setUserConfig(config: IUserConfig): void {
		this.saveToTemp("user_config", JSON.stringify(config));
	}

	getTheme(): Theme {
		return this.getThemeOrDefault();
	}

	getThemeOrDefault(defaultTheme: Theme = Theme.LIGHT): Theme {
		const storedConfig = this.readFromTemp("user_config");

		if (!storedConfig) {
			return defaultTheme;
		}

		try {
			const parsedConfig = JSON.parse(storedConfig) as Partial<IUserConfig> | null;

			if (parsedConfig && parsedConfig.theme && Object.values(Theme).includes(parsedConfig.theme)) {
				return parsedConfig.theme as Theme;
			}
		} catch (error) {
			console.error("Error parsing user configuration from sessionStorage:", error);
		}

		return defaultTheme;
	}

	removeFromTemp(key: string): void {
		sessionStorage.removeItem(key);
	}
}
